import type { DOMOutputSpec, Node as ProsemirrorNode, NodeSpec } from 'prosemirror-model';

import type { RtSchemaType } from '../../editor/types';
import type { AlignAttrs } from '../../extensions/align';
import { AlignExtension } from '../../extensions/align';
import type { HtmlClassAttrs } from '../../extensions/html-class';
import { HtmlClassExtension } from '../../extensions/html-class';
import { NodeName } from '../../schema/nodes';

export interface ImageNodeAttrs extends Partial<AlignAttrs>, Partial<HtmlClassAttrs> {
  src: string;
  width: number | null;
  guid: string;
  alt: string;
  href: string;
  targetBlank: boolean;
  caption: string;
}

const resolveWidth = (element: HTMLElement): number => {
  const maxWidth = element.style.maxWidth || '';
  const width = element.style.width || '';

  const isWidthInPx = width.match('px');
  const isMaxWidthInPx = maxWidth.match('px');

  if (isWidthInPx) {
    return parseInt(width, 10);
  }
  if (isMaxWidthInPx) {
    return parseInt(maxWidth, 10);
  }
  if (element.tagName === 'IMG' && element.parentElement) {
    return resolveWidth(element.parentElement);
  }
  return NaN;
};

export class ImageNode implements NodeSpec {
  public static readonly nodeName = NodeName.Image;
  public static is(node: ProsemirrorNode) {
    return node.type.name === ImageNode.nodeName;
  }
  private readonly alignExtension?: AlignExtension;
  private readonly htmlClassExtension?: HtmlClassExtension;
  public inline = false;
  public draggable = true;
  public group = 'block';
  public attrs = {
    src: { default: '' },
    width: { default: null },
    guid: { default: '' },
    alt: { default: '' },
    href: { default: '' },
    targetBlank: { default: true },
    caption: { default: '' },
    ...(this.config?.withAlign && AlignExtension.attrs),
    ...(this.config?.withHtmlClass && HtmlClassExtension.attrs),
  };
  public parseDOM = [
    {
      tag: 'p',
      priority: 51, // <p><img></p> must resolve before regular <p>Text</p>
      getAttrs: (dom: HTMLElement) => this.parseAttrs(dom),
    },
    {
      tag: 'figure',
      getAttrs: (dom: HTMLElement) => this.parseAttrs(dom),
    },
    {
      tag: 'img',
      getAttrs: (dom: HTMLElement) => this.parseAttrs(dom),
    },
  ];

  constructor(private config?: { withAlign?: boolean; withHtmlClass?: boolean }) {
    if (config?.withAlign) this.alignExtension = new AlignExtension();
    if (config?.withHtmlClass) this.htmlClassExtension = new HtmlClassExtension();
  }

  public toDOM = (node: ProsemirrorNode): DOMOutputSpec => {
    const { href, targetBlank, alt, src, width, caption } = <ImageNodeAttrs>node.attrs;
    const alignStyle = this.alignExtension?.getStyleSerialization(node);
    const htmlClass = this.htmlClassExtension?.getClassSerialization(node);
    const figureEl = <[string, ...any]>[
      'figure',
      {
        ...(alignStyle && {
          style: alignStyle,
        }),
        ...(htmlClass && {
          class: htmlClass,
        }),
      },
    ];
    const captionEl = caption ? <[string, ...any]>['figcaption', {}, caption] : null;

    const linkEl = href
      ? <[string, ...any]>[
          'a',
          {
            href,
            ...(targetBlank && { target: '_blank' }),
          },
        ]
      : null;
    const imgEl = <[string, ...any]>[
      'img',
      {
        src,
        ...(alt && { alt }),
        ...(width && { style: `width:${width}px` }),
      },
    ];

    if (linkEl) {
      linkEl.push(imgEl);
      figureEl.push(linkEl);
    } else {
      figureEl.push(imgEl);
    }

    if (captionEl) {
      figureEl.push(captionEl);
    }
    return figureEl;
  };

  private parseAttrs(dom: HTMLElement): ImageNodeAttrs | false {
    let imgEl: HTMLImageElement | null;
    if (dom.tagName === 'IMG') {
      imgEl = <HTMLImageElement>dom;
    } else {
      imgEl = dom.querySelector('img');
    }
    if (!imgEl) {
      return false;
    }

    const width = resolveWidth(imgEl);
    const href = dom.querySelector('a')?.getAttribute('href') || '';
    const targetBlank = dom.querySelector('a')?.getAttribute('target') === '_blank';
    const caption = dom.querySelector('figcaption')?.textContent || '';
    const align = this.alignExtension?.parseDOMGetAttrs(dom, 'float') || {};
    const html_class = this.htmlClassExtension?.parseDOMGetAttrs(dom) || {};

    return {
      src: imgEl.getAttribute('src') || '',
      alt: imgEl.getAttribute('alt') || '',
      width: isNaN(width) ? null : width,
      guid: '',
      href,
      targetBlank,
      caption,
      ...align,
      ...html_class,
    };
  }
}

export type ImageSchema = RtSchemaType<typeof ImageNode>;
